import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "../Styles/TermsConditions.css";

export default function TermsConditions() {
  return (
    <>
      <Helmet>
        <title>Terms and Conditions | VE Abroad Services</title>
        <meta name="description" content="Read the terms of service for Vidya Education Abroad. Understand the rules, rights, and responsibilities governing our immigration and coaching consultancies." />
        <link rel="canonical" href="https://veabroad.com/terms" />
      </Helmet>

      <section className="terms-hero">
        <h1>Terms & Conditions</h1>
        <p>Last updated: January 1, 2026</p>
      </section>

      <div className="terms-container container">

        <div className="terms-intro">
          <p>
            Welcome to <strong>Vidya Education Abroad</strong> ("we", "our", or "us"). By accessing or using our website <strong>veabroad.com</strong> or engaging with any of our services, you agree to be bound by these Terms and Conditions. Please read them carefully before proceeding.
          </p>
          <p>
            If you do not agree to these terms, please do not use our website or services.
          </p>
        </div>

        <div className="terms-section">
          <h2>1. About Our Services</h2>
          <p>
            Vidya Education Abroad provides consultancy services related to international education, immigration, study visas, work visas, tourist visas, and IELTS coaching. Our services include but are not limited to:
          </p>
          <ul>
            <li>Canada Permanent Residency (PR) guidance</li>
            <li>Study abroad counselling and university applications</li>
            <li>Student visa and work permit assistance</li>
            <li>Tourist and visitor visa support</li>
            <li>IELTS and language test coaching</li>
            <li>Immigration pathway assessment and documentation support</li>
          </ul>
          <p>
            We act as consultants and advisors. Final decisions on visa approvals, admissions, and immigration outcomes rest solely with the respective government authorities and institutions.
          </p>
        </div>

        <div className="terms-section">
          <h2>2. Use of Website</h2>
          <p>By using this website, you agree to:</p>
          <ul>
            <li>Use the website only for lawful purposes</li>
            <li>Not misuse, disrupt, or attempt to gain unauthorized access to any part of the website</li>
            <li>Not submit false, misleading, or fraudulent information through any form</li>
            <li>Not reproduce, duplicate, or copy any content from this website without our written permission</li>
          </ul>
          <p>We reserve the right to restrict or terminate access to the website for any user who violates these terms.</p>
        </div>

        <div className="terms-section">
          <h2>3. Accuracy of Information</h2>
          <p>
            We strive to ensure that all information on this website is accurate and up to date. However, immigration laws, visa policies, tuition fees, and university requirements change frequently. The content on this website is for general informational purposes only and should not be treated as legal or official immigration advice.
          </p>
          <p>
            We strongly recommend verifying all information with the relevant government authority or institution before making any decisions.
          </p>
        </div>

        <div className="terms-section">
          <h2>4. No Guarantee of Outcomes</h2>
          <p>
            Vidya Education Abroad does not guarantee any specific outcome, including but not limited to:
          </p>
          <ul>
            <li>Visa approval or rejection</li>
            <li>University admission</li>
            <li>Permanent Residency approval</li>
            <li>Job offers or employment abroad</li>
          </ul>
          <p>
            While we provide expert guidance and support to maximize your chances of success, all final decisions are made by the respective immigration authorities, embassies, or educational institutions.
          </p>
        </div>

        <div className="terms-section">
          <h2>5. Fees and Payments</h2>
          <p>
            Service fees for consultancy, documentation, and application assistance will be communicated to you clearly before any engagement. By agreeing to our services, you acknowledge and accept the applicable fees.
          </p>
          <ul>
            <li>Fees paid for consultancy services are generally non-refundable unless otherwise agreed in writing</li>
            <li>Government fees, university application fees, and third-party charges are separate and not included in our consultancy fees</li>
            <li>We are not responsible for any losses arising from government fee changes or application rejections</li>
          </ul>
        </div>

        <div className="terms-section">
          <h2>6. Client Responsibilities</h2>
          <p>As a client, you are responsible for:</p>
          <ul>
            <li>Providing accurate, complete, and truthful information and documents</li>
            <li>Informing us of any changes in your personal circumstances that may affect your application</li>
            <li>Meeting all deadlines for document submission and application filing</li>
            <li>Disclosing any previous visa refusals, immigration violations, or criminal history</li>
          </ul>
          <p>
            We are not liable for any adverse outcomes resulting from incomplete, inaccurate, or withheld information provided by the client.
          </p>
        </div>

        <div className="terms-section">
          <h2>7. Intellectual Property</h2>
          <p>
            All content on this website — including text, images, logos, blog articles, and graphics — is the intellectual property of Vidya Education Abroad unless otherwise stated. You may not reproduce, distribute, or use any content without our prior written consent.
          </p>
        </div>

        <div className="terms-section">
          <h2>8. Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites such as government portals, university websites, or partner organizations. These links are provided for your convenience only. We do not endorse or take responsibility for the content, accuracy, or practices of any third-party websites.
          </p>
        </div>

        <div className="terms-section">
          <h2>9. Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by law, Vidya Education Abroad shall not be liable for any direct, indirect, incidental, or consequential damages arising from:
          </p>
          <ul>
            <li>Use or inability to use our website or services</li>
            <li>Visa or application rejections by government authorities</li>
            <li>Errors or omissions in information provided on the website</li>
            <li>Any unauthorized access to or alteration of your data</li>
          </ul>
        </div>

        <div className="terms-section">
          <h2>10. Privacy</h2>
          <p>
            Your use of this website is also governed by our <Link to="/privacy" className="terms-link">Privacy Policy</Link>, which is incorporated into these Terms and Conditions by reference. Please review it to understand our data practices.
          </p>
        </div>

        <div className="terms-section">
          <h2>11. Changes to These Terms</h2>
          <p>
            We reserve the right to update or modify these Terms and Conditions at any time. Changes will be posted on this page with an updated "Last updated" date. Continued use of the website after any changes constitutes your acceptance of the new terms.
          </p>
        </div>

        <div className="terms-section">
          <h2>12. Governing Law</h2>
          <p>
            These Terms and Conditions are governed by and construed in accordance with the laws of India. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in Mumbai, Maharashtra.
          </p>
        </div>

        <div className="terms-section">
          <h2>13. Contact Us</h2>
          <p>If you have any questions about these Terms and Conditions, please reach out to us:</p>
          <ul>
            <li><strong>Email:</strong> info@veabroad.com</li>
            <li><strong>Phone:</strong> +91 8850285886</li>
            <li><strong>Address:</strong> Amar Jyoti CHS, Andheri West, Mumbai, Maharashtra, India</li>
          </ul>
        </div>

        <div className="terms-cta">
          <p>Have questions about our services or terms? We're here to help.</p>
          <Link to="/consultationForm" className="lmbtn">Get Free Consultation</Link>
        </div>

      </div>
    </>
  );
}
